// ABOUTME: The Change cell of a bulk change row: the inline diff of a replaced text (matches struck through / added),
// ABOUTME: "before → after" for the other values and "+ name" / "− name" for links; html is never rendered.

import { alpha, Button, Stack, styled } from '@ds/coolshop';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { ValueList } from '../types';
import {
    computeDiffSegments,
    DEFAULT_DIFF_CONTEXT,
    FULL_DIFF_CONTEXT,
    Segment,
} from '../utils/computeDiffSegments';
import { linkIdOf } from '../utils/valueDisplay';

const DiffText = styled('div')({
    fontFamily: 'monospace',
    fontSize: '0.8125rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxWidth: 640,
});

const RemovedText = styled('span')(({ theme }) => ({
    color: theme.palette.error.main,
    backgroundColor: alpha(theme.palette.error.main, 0.12),
    textDecoration: 'line-through',
}));

const AddedText = styled('span')(({ theme }) => ({
    color: theme.palette.success.main,
    backgroundColor: alpha(theme.palette.success.main, 0.12),
}));

const EllipsisText = styled('span')(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const renderSegment = (segment: Segment, index: number) => {
    switch (segment.kind) {
        case 'removed':
            return (
                <RemovedText key={index} data-kind="removed">
                    {segment.text}
                </RemovedText>
            );
        case 'added':
            return (
                <AddedText key={index} data-kind="added">
                    {segment.text}
                </AddedText>
            );
        case 'ellipsis':
            return (
                <EllipsisText key={index} data-kind="ellipsis">
                    {segment.text}
                </EllipsisText>
            );
        default:
            return <span key={index}>{segment.text}</span>;
    }
};

/** Renders diff segments as text: React escapes it, so html markup shows as source. */
export function DiffSegmentsView({ segments }: { segments: Segment[] }) {
    return <DiffText>{segments.map(renderSegment)}</DiffText>;
}

type DiffCellProps = {
    before: string | null;
    /** `null` when the scan could not compute the replacement (regex timeout). */
    after: string | null;
    /** The run's find pattern, `null` when it does not compile in this browser. */
    regex: RegExp | null;
    replace: string;
};

export function DiffCell({ before: beforeOrNull, after, regex, replace }: DiffCellProps) {
    const { formatMessage } = useIntl();
    const [expanded, setExpanded] = useState(false);
    const before = beforeOrNull ?? '';

    const collapsed = useMemo(
        () =>
            after === null || !regex
                ? null
                : computeDiffSegments(before, regex, replace, DEFAULT_DIFF_CONTEXT),
        [before, after, regex, replace],
    );
    const full = useMemo(
        () =>
            expanded && collapsed && regex
                ? computeDiffSegments(before, regex, replace, FULL_DIFF_CONTEXT)
                : null,
        [expanded, collapsed, before, regex, replace],
    );

    if (!collapsed) return <DiffText>{before}</DiffText>;

    return (
        <Stack gap={0.5} alignItems="flex-start">
            <DiffSegmentsView segments={(full ?? collapsed).segments} />
            {collapsed.truncated && (
                <Button variant="text" size="small" onClick={() => setExpanded(!expanded)}>
                    {expanded
                        ? formatMessage({
                              id: 'findReplace.diff.collapse',
                              defaultMessage: 'Show less',
                          })
                        : formatMessage({
                              id: 'findReplace.diff.expand',
                              defaultMessage: 'Show full value',
                          })}
                </Button>
            )}
        </Stack>
    );
}

const ArrowText = styled('span')(({ theme }) => ({
    color: theme.palette.text.secondary,
    margin: '0 0.5em',
}));

type ValueChangeCellProps = {
    beforeValue: ValueList;
    afterValue: ValueList;
    /** The text of one stored value (a select option label, "12 kg"...). */
    valueText: (item: unknown) => string;
};

/** "before → after" of a whole value list; an absent own value reads "(empty)". */
export function ValueChangeCell({ beforeValue, afterValue, valueText }: ValueChangeCellProps) {
    const { formatMessage } = useIntl();
    const empty = formatMessage({ id: 'bulkChange.change.empty', defaultMessage: '(empty)' });
    const text = (list: ValueList) => (list?.length ? list.map(valueText).join(', ') : null);
    const before = text(beforeValue);
    const after = text(afterValue);
    return (
        <DiffText data-testid="bulk-change-value-change">
            {before === null ? (
                <EllipsisText>{empty}</EllipsisText>
            ) : (
                <RemovedText data-kind="removed">{before}</RemovedText>
            )}
            <ArrowText>→</ArrowText>
            {after === null ? (
                <EllipsisText>{empty}</EllipsisText>
            ) : (
                <AddedText data-kind="added">{after}</AddedText>
            )}
        </DiffText>
    );
}

/** The links a row adds ("+ name") and removes ("− name"). */
export function LinkChangeCell({
    beforeValue,
    afterValue,
    nodeLabel,
}: {
    beforeValue: ValueList;
    afterValue: ValueList;
    nodeLabel: (id: string) => string;
}) {
    const beforeIds = (beforeValue ?? []).map(linkIdOf);
    const afterIds = (afterValue ?? []).map(linkIdOf);
    const added = afterIds.filter(id => !beforeIds.includes(id));
    const removed = beforeIds.filter(id => !afterIds.includes(id));
    return (
        <Stack gap={0.25} data-testid="bulk-change-link-change">
            {added.map(id => (
                <AddedText key={`+${id}`} data-kind="added">{`+ ${nodeLabel(id)}`}</AddedText>
            ))}
            {removed.map(id => (
                <RemovedText key={`-${id}`} data-kind="removed">{`− ${nodeLabel(id)}`}</RemovedText>
            ))}
        </Stack>
    );
}
